import { useState, useEffect, useRef } from 'react'
import {
  ArrowRight, ArrowLeft, CheckCircle, Shield, MapPin, Clock,
  Zap, Users, Star, Home, Building2, Store, HelpCircle,
  Wrench, Paintbrush, Hammer, Key, Snowflake, Truck,
  CalendarClock, Calendar, AlertTriangle, Euro, TrendingUp, Phone,
} from 'lucide-react'
import { formatPhone } from '~/hooks/use-formatter'

const SERVICE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  plomberie: Wrench, electricite: Zap, peinture: Paintbrush,
  renovation: Hammer, menuiserie: Hammer, toiture: Home,
  serrurerie: Key, climatisation: Snowflake, demenagement: Truck,
}

// ─── Types ───────────────────────────────────────────────────

interface InlineQuizProps {
  serviceName: string
  cityName: string
  categorySlug: string
}

interface QuizOption {
  value: string
  label: string
  sublabel?: string
  icon?: React.ComponentType<{ className?: string }>
  emoji?: string
  color?: string
}

// ─── Service-specific quiz data ──────────────────────────────

interface ServiceQuizData {
  projectTypes: QuizOption[]
  detailQuestion: string
  detailOptions: QuizOption[]
  priceEstimate: Record<string, { min: number; max: number }>
}

const SERVICE_QUIZ: Record<string, ServiceQuizData> = {
  plomberie: {
    projectTypes: [
      { value: 'fuite', label: 'Fuite d\'eau', sublabel: 'Robinet, tuyau, WC, chauffe-eau', icon: AlertTriangle, color: 'border-red-200 hover:bg-red-50' },
      { value: 'debouchage', label: 'Débouchage', sublabel: 'Évier, WC, douche, canalisation', icon: Wrench, color: 'border-amber-200 hover:bg-amber-50' },
      { value: 'installation', label: 'Installation', sublabel: 'Sanitaires, chauffe-eau, robinetterie', icon: Home, color: 'border-blue-200 hover:bg-blue-50' },
      { value: 'renovation', label: 'Rénovation plomberie', sublabel: 'Salle de bain, cuisine, complète', icon: Hammer, color: 'border-emerald-200 hover:bg-emerald-50' },
    ],
    detailQuestion: 'Pouvez-vous préciser ?',
    detailOptions: [
      { value: 'fuite_visible', label: 'Fuite visible (goutte à goutte)' },
      { value: 'fuite_importante', label: 'Fuite importante (eau qui coule)' },
      { value: 'fuite_cachee', label: 'Fuite cachée (trace d\'humidité)' },
      { value: 'remplacement', label: 'Remplacement d\'équipement' },
      { value: 'creation', label: 'Création / nouvelle installation' },
      { value: 'autre', label: 'Autre / je ne sais pas' },
    ],
    priceEstimate: { fuite: { min: 80, max: 250 }, debouchage: { min: 100, max: 350 }, installation: { min: 200, max: 800 }, renovation: { min: 1500, max: 5000 } },
  },
  electricite: {
    projectTypes: [
      { value: 'depannage', label: 'Dépannage', sublabel: 'Panne, court-circuit, disjoncteur', icon: Zap, color: 'border-red-200 hover:bg-red-50' },
      { value: 'normes', label: 'Mise aux normes', sublabel: 'Tableau, NF C 15-100', icon: Shield, color: 'border-amber-200 hover:bg-amber-50' },
      { value: 'installation', label: 'Installation', sublabel: 'Prises, éclairage, radiateurs', icon: Home, color: 'border-blue-200 hover:bg-blue-50' },
      { value: 'renovation', label: 'Rénovation complète', sublabel: 'Refonte totale de l\'installation', icon: Hammer, color: 'border-emerald-200 hover:bg-emerald-50' },
    ],
    detailQuestion: 'Type de logement ?',
    detailOptions: [
      { value: 'studio', label: 'Studio / T1 (< 30 m²)' }, { value: 't2_t3', label: 'T2-T3 (30-70 m²)' },
      { value: 't4_plus', label: 'T4+ (> 70 m²)' }, { value: 'maison', label: 'Maison individuelle' },
      { value: 'local', label: 'Local commercial' }, { value: 'autre', label: 'Autre' },
    ],
    priceEstimate: { depannage: { min: 80, max: 200 }, normes: { min: 1000, max: 3000 }, installation: { min: 200, max: 1000 }, renovation: { min: 3000, max: 8000 } },
  },
  peinture: {
    projectTypes: [
      { value: 'interieur', label: 'Peinture intérieure', sublabel: 'Murs, plafonds, boiseries', icon: Paintbrush, color: 'border-blue-200 hover:bg-blue-50' },
      { value: 'exterieur', label: 'Peinture extérieure', sublabel: 'Façade, volets, portail', icon: Home, color: 'border-amber-200 hover:bg-amber-50' },
      { value: 'papier', label: 'Papier peint', sublabel: 'Pose, dépose, tapisserie', icon: Hammer, color: 'border-emerald-200 hover:bg-emerald-50' },
      { value: 'decoratif', label: 'Peinture décorative', sublabel: 'Effet béton, stuc, métallisé', icon: Star, color: 'border-violet-200 hover:bg-violet-50' },
    ],
    detailQuestion: 'Quelle surface approximative ?',
    detailOptions: [
      { value: 'petite', label: 'Petite (< 20 m²)', sublabel: 'Une pièce, un couloir' },
      { value: 'moyenne', label: 'Moyenne (20-60 m²)', sublabel: '2-3 pièces' },
      { value: 'grande', label: 'Grande (60-120 m²)', sublabel: 'Appartement complet' },
      { value: 'tres_grande', label: 'Très grande (> 120 m²)', sublabel: 'Maison, local' },
    ],
    priceEstimate: { interieur: { min: 400, max: 3000 }, exterieur: { min: 1500, max: 8000 }, papier: { min: 300, max: 2000 }, decoratif: { min: 800, max: 5000 } },
  },
  renovation: {
    projectTypes: [
      { value: 'sdb', label: 'Salle de bain', sublabel: 'Complète ou partielle', icon: Wrench, color: 'border-blue-200 hover:bg-blue-50' },
      { value: 'cuisine', label: 'Cuisine', sublabel: 'Meubles, plan de travail, électro', icon: Home, color: 'border-amber-200 hover:bg-amber-50' },
      { value: 'appartement', label: 'Appartement complet', sublabel: 'Tous corps d\'état', icon: Building2, color: 'border-emerald-200 hover:bg-emerald-50' },
      { value: 'extension', label: 'Extension / surélévation', sublabel: 'Agrandissement, combles', icon: Hammer, color: 'border-violet-200 hover:bg-violet-50' },
    ],
    detailQuestion: 'Quel niveau de rénovation ?',
    detailOptions: [
      { value: 'rafraichissement', label: 'Rafraîchissement', sublabel: 'Peinture, sol, petits travaux' },
      { value: 'moyenne', label: 'Rénovation moyenne', sublabel: 'Plomberie, électricité, revêtements' },
      { value: 'lourde', label: 'Rénovation lourde', sublabel: 'Structure, isolation, tout refaire' },
      { value: 'ne_sait_pas', label: 'Je ne sais pas encore', sublabel: 'Un pro m\'aidera à décider' },
    ],
    priceEstimate: { sdb: { min: 5000, max: 15000 }, cuisine: { min: 8000, max: 25000 }, appartement: { min: 20000, max: 60000 }, extension: { min: 30000, max: 80000 } },
  },
  menuiserie: {
    projectTypes: [
      { value: 'fenetres', label: 'Fenêtres / vitrages', sublabel: 'Remplacement, double vitrage', icon: Home, color: 'border-blue-200 hover:bg-blue-50' },
      { value: 'portes', label: 'Portes', sublabel: 'Intérieures, extérieures, blindées', icon: Key, color: 'border-amber-200 hover:bg-amber-50' },
      { value: 'placards', label: 'Placards / Dressing', sublabel: 'Sur mesure, aménagement', icon: Store, color: 'border-emerald-200 hover:bg-emerald-50' },
      { value: 'parquet', label: 'Parquet / Escalier', sublabel: 'Pose, rénovation, ponçage', icon: Hammer, color: 'border-violet-200 hover:bg-violet-50' },
    ],
    detailQuestion: 'Combien d\'éléments ?',
    detailOptions: [
      { value: '1', label: '1 élément' }, { value: '2_3', label: '2-3 éléments' },
      { value: '4_6', label: '4-6 éléments' }, { value: '7_plus', label: '7 ou plus' },
    ],
    priceEstimate: { fenetres: { min: 300, max: 1500 }, portes: { min: 200, max: 800 }, placards: { min: 500, max: 3000 }, parquet: { min: 1000, max: 5000 } },
  },
  toiture: {
    projectTypes: [
      { value: 'reparation', label: 'Réparation', sublabel: 'Tuiles cassées, fuite, gouttière', icon: Wrench, color: 'border-red-200 hover:bg-red-50' },
      { value: 'refection', label: 'Réfection complète', sublabel: 'Remplacement total de couverture', icon: Home, color: 'border-amber-200 hover:bg-amber-50' },
      { value: 'nettoyage', label: 'Nettoyage / démoussage', sublabel: 'Traitement anti-mousse', icon: Hammer, color: 'border-blue-200 hover:bg-blue-50' },
      { value: 'isolation', label: 'Isolation toiture', sublabel: 'Sous rampants, combles perdus', icon: Shield, color: 'border-emerald-200 hover:bg-emerald-50' },
    ],
    detailQuestion: 'Surface approximative de toiture ?',
    detailOptions: [
      { value: 'petite', label: 'Petite (< 50 m²)' }, { value: 'moyenne', label: 'Moyenne (50-100 m²)' },
      { value: 'grande', label: 'Grande (100-200 m²)' }, { value: 'ne_sait_pas', label: 'Je ne sais pas' },
    ],
    priceEstimate: { reparation: { min: 200, max: 2000 }, refection: { min: 10000, max: 30000 }, nettoyage: { min: 500, max: 2000 }, isolation: { min: 2000, max: 8000 } },
  },
  serrurerie: {
    projectTypes: [
      { value: 'ouverture', label: 'Ouverture de porte', sublabel: 'Claquée ou fermée à clé', icon: Key, color: 'border-red-200 hover:bg-red-50' },
      { value: 'serrure', label: 'Changement serrure', sublabel: 'Standard, multipoints', icon: Wrench, color: 'border-amber-200 hover:bg-amber-50' },
      { value: 'blindage', label: 'Blindage de porte', sublabel: 'Porte blindée, renforcement', icon: Shield, color: 'border-blue-200 hover:bg-blue-50' },
      { value: 'digicode', label: 'Digicode / Interphone', sublabel: 'Installation, remplacement', icon: Home, color: 'border-emerald-200 hover:bg-emerald-50' },
    ],
    detailQuestion: 'C\'est urgent ?',
    detailOptions: [
      { value: 'bloque_dehors', label: 'Je suis bloqué(e) dehors', sublabel: 'Urgence immédiate' },
      { value: 'planifie', label: 'C\'est planifié', sublabel: 'Pas d\'urgence' },
      { value: 'cambriolage', label: 'Suite à un cambriolage', sublabel: 'Intervention rapide' },
      { value: 'preventif', label: 'Amélioration sécurité', sublabel: 'Renforcement préventif' },
    ],
    priceEstimate: { ouverture: { min: 80, max: 250 }, serrure: { min: 100, max: 400 }, blindage: { min: 800, max: 2500 }, digicode: { min: 200, max: 1000 } },
  },
  climatisation: {
    projectTypes: [
      { value: 'installation', label: 'Installation clim', sublabel: 'Split, multi-split, gainable', icon: Snowflake, color: 'border-blue-200 hover:bg-blue-50' },
      { value: 'entretien', label: 'Entretien annuel', sublabel: 'Nettoyage, recharge', icon: Wrench, color: 'border-emerald-200 hover:bg-emerald-50' },
      { value: 'pac', label: 'Pompe à chaleur', sublabel: 'Air-air, air-eau, géothermie', icon: Home, color: 'border-amber-200 hover:bg-amber-50' },
      { value: 'vmc', label: 'VMC', sublabel: 'Simple flux, double flux', icon: Zap, color: 'border-violet-200 hover:bg-violet-50' },
    ],
    detailQuestion: 'Nombre de pièces à équiper ?',
    detailOptions: [
      { value: '1', label: '1 pièce' }, { value: '2_3', label: '2-3 pièces' },
      { value: '4_plus', label: '4 pièces ou plus' }, { value: 'maison', label: 'Maison entière' },
    ],
    priceEstimate: { installation: { min: 1500, max: 5000 }, entretien: { min: 80, max: 200 }, pac: { min: 5000, max: 15000 }, vmc: { min: 500, max: 3000 } },
  },
  nettoyage: {
    projectTypes: [
      { value: 'menage', label: 'Ménage régulier', sublabel: 'Hebdo, bi-mensuel', icon: Home, color: 'border-blue-200 hover:bg-blue-50' },
      { value: 'chantier', label: 'Fin de chantier', sublabel: 'Nettoyage après travaux', icon: Hammer, color: 'border-amber-200 hover:bg-amber-50' },
      { value: 'vitres', label: 'Nettoyage vitres', sublabel: 'Intérieur, extérieur', icon: Star, color: 'border-emerald-200 hover:bg-emerald-50' },
      { value: 'copro', label: 'Copropriété', sublabel: 'Parties communes', icon: Building2, color: 'border-violet-200 hover:bg-violet-50' },
    ],
    detailQuestion: 'Surface à nettoyer ?',
    detailOptions: [
      { value: 'petit', label: 'Petit (< 50 m²)' }, { value: 'moyen', label: 'Moyen (50-100 m²)' },
      { value: 'grand', label: 'Grand (100-200 m²)' }, { value: 'tres_grand', label: 'Très grand (> 200 m²)' },
    ],
    priceEstimate: { menage: { min: 60, max: 150 }, chantier: { min: 200, max: 800 }, vitres: { min: 80, max: 300 }, copro: { min: 300, max: 1500 } },
  },
  jardinage: {
    projectTypes: [
      { value: 'tonte', label: 'Tonte / Entretien', sublabel: 'Pelouse, haies, désherbage', icon: Home, color: 'border-emerald-200 hover:bg-emerald-50' },
      { value: 'elagage', label: 'Élagage / Abattage', sublabel: 'Arbres, branches, souches', icon: Hammer, color: 'border-amber-200 hover:bg-amber-50' },
      { value: 'creation', label: 'Création jardin', sublabel: 'Aménagement, terrasse', icon: Star, color: 'border-blue-200 hover:bg-blue-50' },
      { value: 'cloture', label: 'Clôture / Portail', sublabel: 'Installation, remplacement', icon: Shield, color: 'border-violet-200 hover:bg-violet-50' },
    ],
    detailQuestion: 'Surface du jardin ?',
    detailOptions: [
      { value: 'petit', label: 'Petit (< 100 m²)' }, { value: 'moyen', label: 'Moyen (100-500 m²)' },
      { value: 'grand', label: 'Grand (500-1000 m²)' }, { value: 'tres_grand', label: 'Très grand (> 1000 m²)' },
    ],
    priceEstimate: { tonte: { min: 30, max: 100 }, elagage: { min: 200, max: 1500 }, creation: { min: 2000, max: 10000 }, cloture: { min: 1000, max: 5000 } },
  },
  demenagement: {
    projectTypes: [
      { value: 'local', label: 'Déménagement local', sublabel: 'Même ville', icon: Truck, color: 'border-blue-200 hover:bg-blue-50' },
      { value: 'longue', label: 'Longue distance', sublabel: 'Autre ville ou région', icon: MapPin, color: 'border-amber-200 hover:bg-amber-50' },
      { value: 'meuble', label: 'Garde-meuble', sublabel: 'Stockage temporaire', icon: Store, color: 'border-emerald-200 hover:bg-emerald-50' },
      { value: 'monte', label: 'Monte-meuble', sublabel: 'Objets lourds, étages', icon: Home, color: 'border-violet-200 hover:bg-violet-50' },
    ],
    detailQuestion: 'Taille du logement actuel ?',
    detailOptions: [
      { value: 'studio', label: 'Studio / T1' }, { value: 't2', label: 'T2 (2 pièces)' },
      { value: 't3_t4', label: 'T3-T4 (3-4 pièces)' }, { value: 't5_plus', label: 'T5+ ou maison' },
    ],
    priceEstimate: { local: { min: 300, max: 1500 }, longue: { min: 1000, max: 5000 }, meuble: { min: 50, max: 200 }, monte: { min: 200, max: 800 } },
  },
  'carrelage-sol': {
    projectTypes: [
      { value: 'sol', label: 'Carrelage sol', sublabel: 'Cuisine, salon, entrée', icon: Home, color: 'border-blue-200 hover:bg-blue-50' },
      { value: 'mural', label: 'Carrelage mural', sublabel: 'Salle de bain, crédence', icon: Hammer, color: 'border-amber-200 hover:bg-amber-50' },
      { value: 'terrasse', label: 'Terrasse extérieure', sublabel: 'Dalles, grès cérame', icon: Star, color: 'border-emerald-200 hover:bg-emerald-50' },
      { value: 'depose', label: 'Dépose ancien', sublabel: 'Retrait + ragréage', icon: Wrench, color: 'border-red-200 hover:bg-red-50' },
    ],
    detailQuestion: 'Surface à carreler ?',
    detailOptions: [
      { value: 'petite', label: 'Petite (< 10 m²)', sublabel: 'Douche, crédence' },
      { value: 'moyenne', label: 'Moyenne (10-30 m²)', sublabel: 'SDB, cuisine' },
      { value: 'grande', label: 'Grande (30-60 m²)', sublabel: 'Pièce de vie' },
      { value: 'tres_grande', label: 'Très grande (> 60 m²)', sublabel: 'Appartement complet' },
    ],
    priceEstimate: { sol: { min: 500, max: 3000 }, mural: { min: 400, max: 2000 }, terrasse: { min: 1000, max: 5000 }, depose: { min: 300, max: 1500 } },
  },
  isolation: {
    projectTypes: [
      { value: 'murs_int', label: 'Isolation murs intérieurs', sublabel: 'Doublage, laine, panneaux', icon: Home, color: 'border-blue-200 hover:bg-blue-50' },
      { value: 'murs_ext', label: 'Isolation extérieure (ITE)', sublabel: 'Bardage, enduit sur isolant', icon: Building2, color: 'border-amber-200 hover:bg-amber-50' },
      { value: 'combles', label: 'Isolation combles', sublabel: 'Perdus ou aménagés', icon: Hammer, color: 'border-emerald-200 hover:bg-emerald-50' },
      { value: 'phonique', label: 'Isolation phonique', sublabel: 'Bruit voisins, rue', icon: Shield, color: 'border-violet-200 hover:bg-violet-50' },
    ],
    detailQuestion: 'Surface à isoler ?',
    detailOptions: [
      { value: 'petite', label: 'Petite (< 30 m²)' }, { value: 'moyenne', label: 'Moyenne (30-80 m²)' },
      { value: 'grande', label: 'Grande (80-150 m²)' }, { value: 'maison', label: 'Maison complète' },
    ],
    priceEstimate: { murs_int: { min: 1000, max: 4000 }, murs_ext: { min: 5000, max: 15000 }, combles: { min: 1500, max: 5000 }, phonique: { min: 800, max: 3000 } },
  },
  maconnerie: {
    projectTypes: [
      { value: 'mur', label: 'Mur / Cloison', sublabel: 'Construction, démolition', icon: Hammer, color: 'border-blue-200 hover:bg-blue-50' },
      { value: 'dalle', label: 'Dalle béton / Chape', sublabel: 'Garage, terrasse', icon: Home, color: 'border-amber-200 hover:bg-amber-50' },
      { value: 'ravalement', label: 'Ravalement façade', sublabel: 'Nettoyage, enduit', icon: Paintbrush, color: 'border-emerald-200 hover:bg-emerald-50' },
      { value: 'terrassement', label: 'Terrassement', sublabel: 'Fondations, drainage', icon: Wrench, color: 'border-violet-200 hover:bg-violet-50' },
    ],
    detailQuestion: 'Ampleur du projet ?',
    detailOptions: [
      { value: 'petit', label: 'Petit (1-2 jours)' }, { value: 'moyen', label: 'Moyen (3-5 jours)' },
      { value: 'grand', label: 'Grand (1-2 semaines)' }, { value: 'ne_sait_pas', label: 'Je ne sais pas' },
    ],
    priceEstimate: { mur: { min: 300, max: 2000 }, dalle: { min: 1000, max: 5000 }, ravalement: { min: 2000, max: 10000 }, terrassement: { min: 1500, max: 8000 } },
  },
  piscine: {
    projectTypes: [
      { value: 'construction', label: 'Construction piscine', sublabel: 'Coque, béton, liner', icon: Star, color: 'border-blue-200 hover:bg-blue-50' },
      { value: 'entretien', label: 'Entretien piscine', sublabel: 'Hivernage, mise en route', icon: Wrench, color: 'border-emerald-200 hover:bg-emerald-50' },
      { value: 'renovation', label: 'Rénovation liner', sublabel: 'Remplacement, réparation', icon: Hammer, color: 'border-amber-200 hover:bg-amber-50' },
      { value: 'equipement', label: 'Équipement', sublabel: 'Pompe, filtre, chauffage', icon: Home, color: 'border-violet-200 hover:bg-violet-50' },
    ],
    detailQuestion: 'Type de piscine ?',
    detailOptions: [
      { value: 'coque', label: 'Coque polyester' }, { value: 'beton', label: 'Béton / maçonnée' },
      { value: 'hors_sol', label: 'Hors-sol' }, { value: 'ne_sait_pas', label: 'Pas encore décidé' },
    ],
    priceEstimate: { construction: { min: 15000, max: 50000 }, entretien: { min: 100, max: 300 }, renovation: { min: 2000, max: 5000 }, equipement: { min: 500, max: 3000 } },
  },
  securite: {
    projectTypes: [
      { value: 'alarme', label: 'Alarme / Vidéo', sublabel: 'Détecteurs, caméras', icon: Shield, color: 'border-red-200 hover:bg-red-50' },
      { value: 'porte', label: 'Porte blindée', sublabel: 'Installation, remplacement', icon: Key, color: 'border-amber-200 hover:bg-amber-50' },
      { value: 'interphone', label: 'Interphone / Visiophone', sublabel: 'Sonnette vidéo, digicode', icon: Home, color: 'border-blue-200 hover:bg-blue-50' },
      { value: 'coffre', label: 'Coffre-fort', sublabel: 'Encastré, à poser', icon: Star, color: 'border-emerald-200 hover:bg-emerald-50' },
    ],
    detailQuestion: 'Type de bien à sécuriser ?',
    detailOptions: [
      { value: 'appartement', label: 'Appartement' }, { value: 'maison', label: 'Maison' },
      { value: 'commerce', label: 'Commerce / Bureau' }, { value: 'autre', label: 'Autre' },
    ],
    priceEstimate: { alarme: { min: 500, max: 3000 }, porte: { min: 1500, max: 4000 }, interphone: { min: 200, max: 1000 }, coffre: { min: 200, max: 1000 } },
  },
}

const DEFAULT_QUIZ: ServiceQuizData = {
  projectTypes: [
    { value: 'petit', label: 'Petit projet', sublabel: 'Dépannage, réparation rapide', icon: Wrench },
    { value: 'moyen', label: 'Projet moyen', sublabel: 'Installation, remplacement', icon: Hammer },
    { value: 'grand', label: 'Grand projet', sublabel: 'Rénovation, création complète', icon: Home },
    { value: 'urgence', label: 'Urgence', sublabel: 'Intervention rapide', icon: AlertTriangle, color: 'border-red-200 hover:bg-red-50' },
  ],
  detailQuestion: 'Type de bien ?',
  detailOptions: [
    { value: 'appartement', label: 'Appartement' }, { value: 'maison', label: 'Maison' },
    { value: 'commercial', label: 'Local commercial' }, { value: 'autre', label: 'Autre' },
  ],
  priceEstimate: { petit: { min: 80, max: 300 }, moyen: { min: 500, max: 2000 }, grand: { min: 2000, max: 10000 }, urgence: { min: 150, max: 500 } },
}

const URGENCY_OPTIONS: QuizOption[] = [
  { value: 'urgent_24h', label: 'Urgent — sous 24h', sublabel: 'Problème actif', icon: AlertTriangle, color: 'border-red-200 hover:bg-red-50' },
  { value: 'within_days', label: 'Sous quelques jours', sublabel: 'Pas urgent mais rapide', icon: CalendarClock, color: 'border-amber-200 hover:bg-amber-50' },
  { value: 'within_week', label: 'Dans la semaine', sublabel: 'Je peux attendre', icon: Calendar, color: 'border-blue-200 hover:bg-blue-50' },
  { value: 'flexible', label: 'Flexible, pas pressé', sublabel: 'Je compare tranquillement', icon: Clock, color: 'border-slate-200 hover:bg-slate-50' },
]

const PROPERTY_OPTIONS: QuizOption[] = [
  { value: 'apartment', label: 'Appartement', icon: Building2 },
  { value: 'house', label: 'Maison', icon: Home },
  { value: 'commercial', label: 'Local commercial', icon: Store },
  { value: 'other', label: 'Autre', icon: HelpCircle },
]

// ─── Step labels for the mini-stepper ────────────────────────

const STEP_LABELS = ['Projet', 'Détails', 'Délai', 'Bien', 'Contact']

// ─── Animated number counter ─────────────────────────────────

function AnimatedPrice({ value }: { value: number }) {
  const [display, setDisplay] = useState(0)
  useEffect(() => {
    const duration = 600
    const start = performance.now()
    const from = 0
    function animate(now: number) {
      const progress = Math.min((now - start) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setDisplay(Math.round(from + (value - from) * eased))
      if (progress < 1) requestAnimationFrame(animate)
    }
    requestAnimationFrame(animate)
  }, [value])
  return <>{display.toLocaleString('fr-FR')}</>
}

// ─── Component ───────────────────────────────────────────────

export function InlineQuiz({ serviceName, cityName, categorySlug }: InlineQuizProps) {
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [direction, setDirection] = useState<'forward' | 'back'>('forward')
  const [isAnimating, setIsAnimating] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const quizData = SERVICE_QUIZ[categorySlug] ?? DEFAULT_QUIZ
  const totalSteps = 6

  // Contact form state
  const [firstName, setFirstName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [postal, setPostal] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const [refNumber, setRefNumber] = useState('')

  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  // Time-contextual response copy.
  // NOTE: `new Date()` MUST NOT run in render body — SSR clock (possibly hours-stale
  // due to CDN cache) ≠ CSR clock => React hydration mismatch #418 (see commit 4301122).
  // We initialise with a stable, generic copy that SSR + first CSR render produce
  // identically, then upgrade to the time-contextual variant after mount.
  const [responseTimeText, setResponseTimeText] = useState('Réponse sous 48h')
  useEffect(() => {
    const hour = new Date().getHours()
    setResponseTimeText(
      hour < 12 ? 'Réponse avant ce midi' : hour < 17 ? 'Réponse ce soir' : 'Réponse demain matin',
    )
  }, [])

  function selectAnswer(key: string, value: string) {
    setSelectedKey(value)
    setDirection('forward')
    setIsAnimating(true)
    setAnswers((prev) => ({ ...prev, [key]: value }))
    setTimeout(() => {
      setStep((s) => s + 1)
      setIsAnimating(false)
      setSelectedKey(null)
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 300)
  }

  function goBack() {
    if (step > 0) {
      setDirection('back')
      setIsAnimating(true)
      setTimeout(() => {
        setStep(step - 1)
        setIsAnimating(false)
      }, 200)
    }
  }

  async function handleSubmitLead(e: React.FormEvent) {
    e.preventDefault()
    if (!firstName.trim() || !phone.trim() || !email.trim()) {
      setError('Veuillez remplir tous les champs')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Email invalide')
      return
    }
    if (phone.replace(/\s/g, '').length < 10) {
      setError('Numéro de téléphone invalide')
      return
    }
    if (!/^\d{5}$/.test(postal.trim())) {
      setError('Code postal invalide (5 chiffres)')
      return
    }

    setSubmitting(true)
    setError('')

    try {
      const res = await fetch('/api/quiz-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: firstName.trim(),
          phone: phone.trim(),
          email: email.trim(),
          categorySlug,
          city: cityName,
          postalCode: postal.trim(),
          projectType: answers.projectType ?? '',
          detail: answers.detail ?? '',
          urgency: answers.urgency ?? 'flexible',
          property: answers.property ?? 'apartment',
        }),
      })
      const data = await res.json()
      if (data.success) {
        setRefNumber(data.referenceNumber ?? '')
        setSubmitted(true)
        setStep(5)
      } else {
        setError(data.error ?? 'Erreur lors de l\'envoi')
      }
    } catch {
      setError('Erreur réseau. Veuillez réessayer.')
    }
    setSubmitting(false)
  }

  const estimate = quizData.priceEstimate[answers.projectType ?? '']

  // Slide animation class
  const slideClass = isAnimating
    ? direction === 'forward' ? 'translate-x-4 opacity-0' : '-translate-x-4 opacity-0'
    : 'translate-x-0 opacity-100'

  return (
    <div ref={containerRef} className="overflow-hidden rounded-2xl border border-primary/20 bg-white shadow-lg">
      {/* ── Header ── */}
      <div className="bg-gradient-to-r from-primary to-blue-600 px-5 py-3.5 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {(() => { const SvcIcon = SERVICE_ICONS[categorySlug] ?? Zap; return <SvcIcon className="h-4 w-4" /> })()}
            <div>
              <h3 className="text-sm font-bold">Devis gratuit — {serviceName}</h3>
              <p className="text-[11px] text-blue-200">{cityName} · 2 min · Sans engagement</p>
            </div>
          </div>
          {step > 0 && step < 5 && (
            <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-[10px] font-bold tabular-nums">{step}/{totalSteps - 1}</span>
          )}
        </div>

        {/* ── Mini stepper with labels + endowed progress ── */}
        <div className="mt-2.5 flex items-center gap-1">
          {STEP_LABELS.map((label, i) => (
            <div key={label} className="flex-1">
              <div className={`h-1.5 rounded-full transition-all duration-500 ${
                i < step ? 'bg-white' : i === step ? 'bg-white/60 animate-pulse' : 'bg-white/15'
              }`}
              style={i === 0 && step === 0 ? { background: 'rgba(255,255,255,0.35)' } : undefined}
              />
              <p className={`mt-1 text-center text-[8px] font-medium transition-colors ${
                i < step ? 'text-white' : i === step ? 'text-white/80' : 'text-white/30'
              }`}>
                {i < step ? '✓' : label}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Info bar ── */}
      {step < 4 && (
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-1.5">
          <p className="text-[10px] text-slate-500">
            <span className="font-semibold text-slate-700">100% gratuit</span> · Sans engagement · Réponse sous 48h
          </p>
          <div className="flex items-center gap-1.5">
            <Shield className="h-3 w-3 text-emerald-500" />
            <span className="text-[10px] font-medium text-emerald-600">Artisans vérifiés</span>
          </div>
        </div>
      )}

      {/* ── Content ── */}
      <div className={`p-4 sm:p-5 transition-all duration-300 ease-out ${slideClass}`}>

        {/* Step 0: Project type */}
        {step === 0 && (
          <div>
            <p className="text-sm font-bold text-slate-900">Quel type de travaux ?</p>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {quizData.projectTypes.map((opt) => {
                const Icon = opt.icon ?? Wrench
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => selectAnswer('projectType', opt.value)}
                    aria-label={opt.label}
                    className={`group flex items-center gap-3 rounded-xl border-2 px-3.5 py-3 text-left transition-all active:scale-[0.97] ${
                      selectedKey === opt.value ? 'border-primary bg-primary-50 scale-[0.97]' : `bg-white ${opt.color ?? 'border-slate-200 hover:bg-slate-50'}`
                    }`}
                  >
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors ${
                      selectedKey === opt.value ? 'bg-primary' : 'bg-slate-100 group-hover:bg-primary/10'
                    }`}>
                      {selectedKey === opt.value ? (
                        <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>
                      ) : (
                        <Icon className="h-4 w-4 text-slate-500 group-hover:text-primary" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <span className="text-sm font-semibold text-slate-900">{opt.label}</span>
                      {opt.sublabel && <span className="block truncate text-[11px] text-slate-400">{opt.sublabel}</span>}
                    </div>
                    <ArrowRight className="ml-auto h-3.5 w-3.5 shrink-0 text-slate-200 transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Step 1: Detail */}
        {step === 1 && (
          <div>
            {estimate && (
              <div className="mb-3 flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2.5">
                <Euro className="h-4 w-4 text-emerald-600" />
                <span className="text-xs text-emerald-700">Estimation : <strong><AnimatedPrice value={estimate.min} /> — <AnimatedPrice value={estimate.max} /> €</strong></span>
              </div>
            )}
            <p className="text-sm font-bold text-slate-900">{quizData.detailQuestion}</p>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {quizData.detailOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => selectAnswer('detail', opt.value)}
                  className="rounded-xl border-2 border-slate-200 bg-white px-3.5 py-3 text-left transition-all hover:border-primary hover:bg-primary-50 active:scale-[0.97]"
                >
                  <span className="text-sm font-medium text-slate-900">{opt.label}</span>
                  {opt.sublabel && <span className="block text-[11px] text-slate-400">{opt.sublabel}</span>}
                </button>
              ))}
            </div>
            <button type="button" onClick={goBack} className="mt-3 flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-slate-400 transition-colors hover:bg-slate-50 hover:text-primary">
              <ArrowLeft className="h-3.5 w-3.5" /> Retour
            </button>
          </div>
        )}

        {/* Step 2: Urgency */}
        {step === 2 && (
          <div>
            <p className="text-sm font-bold text-slate-900">Quand souhaitez-vous ces travaux ?</p>
            <div className="mt-3 space-y-2">
              {URGENCY_OPTIONS.map((opt) => {
                const Icon = opt.icon ?? Clock
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => selectAnswer('urgency', opt.value)}
                    className={`flex w-full items-center gap-3 rounded-xl border-2 bg-white px-3.5 py-3 text-left transition-all hover:shadow-sm active:scale-[0.98] ${opt.color ?? 'border-slate-200'}`}
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50">
                      <Icon className="h-4 w-4 text-slate-500" />
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-slate-900">{opt.label}</span>
                      <span className="block text-[11px] text-slate-400">{opt.sublabel}</span>
                    </div>
                  </button>
                )
              })}
            </div>
            <button type="button" onClick={goBack} className="mt-3 flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-slate-400 transition-colors hover:bg-slate-50 hover:text-primary">
              <ArrowLeft className="h-3.5 w-3.5" /> Retour
            </button>
          </div>
        )}

        {/* Step 3: Property type */}
        {step === 3 && (
          <div>
            <p className="text-sm font-bold text-slate-900">Quel type de bien ?</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {PROPERTY_OPTIONS.map((opt) => {
                const Icon = opt.icon ?? Home
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => selectAnswer('property', opt.value)}
                    className="flex items-center gap-2.5 rounded-xl border-2 border-slate-200 bg-white px-3.5 py-3.5 text-left transition-all hover:border-primary hover:bg-primary-50 active:scale-[0.97]"
                  >
                    <Icon className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-semibold text-slate-900">{opt.label}</span>
                  </button>
                )
              })}
            </div>
            <button type="button" onClick={goBack} className="mt-3 flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-slate-400 transition-colors hover:bg-slate-50 hover:text-primary">
              <ArrowLeft className="h-3.5 w-3.5" /> Retour
            </button>
          </div>
        )}

        {/* Step 4: Contact form */}
        {step === 4 && !submitted && (
          <div>
            {estimate && (
              <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-500">Estimation pour votre projet</p>
                <p className="mt-0.5 text-xl font-bold text-emerald-800">
                  <AnimatedPrice value={estimate.min} /> — <AnimatedPrice value={estimate.max} /> €
                </p>
                <p className="mt-0.5 text-[10px] text-emerald-600">{responseTimeText}</p>
              </div>
            )}

            <p className="text-sm font-bold text-slate-900">Dernière étape — vous y êtes presque !</p>

            {/* "What happens next" mini-timeline */}
            <div className="mt-2 mb-3 flex items-center gap-1 text-[10px] text-slate-400">
              <span className="flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">1. Matching</span>
              <ArrowRight className="h-2.5 w-2.5" />
              <span className="flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5">2. Devis 48h</span>
              <ArrowRight className="h-2.5 w-2.5" />
              <span className="flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5">3. Vous choisissez</span>
            </div>

            <form onSubmit={handleSubmitLead} className="mt-3 space-y-2.5">
              {error && (
                <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
              )}
              <div>
                <input
                  type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Prénom *" autoComplete="given-name" required
                  className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                <p className="mt-0.5 px-1 text-sm text-slate-500">Pour personnaliser votre devis</p>
              </div>
              <div>
                {phone.length > 0 && phone.replace(/\s/g, '').length < 10 && (
                  <p id="quiz-phone-error" role="alert" className="mb-1 px-1 text-sm text-amber-600">Numéro incomplet — 10 chiffres requis</p>
                )}
                <input
                  type="tel" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))}
                  inputMode="tel"
                  placeholder="06 12 34 56 78 *" autoComplete="tel" required
                  aria-invalid={phone.length > 0 && phone.replace(/\s/g, '').length < 10 ? true : undefined}
                  aria-describedby={phone.length > 0 && phone.replace(/\s/g, '').length < 10 ? 'quiz-phone-error' : undefined}
                  className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                {!(phone.length > 0 && phone.replace(/\s/g, '').length < 10) && (
                  <p className="mt-0.5 px-1 text-sm text-slate-500">Les pros vous rappellent directement</p>
                )}
              </div>
              <div>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email *" autoComplete="email" required
                  className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                <p className="mt-0.5 px-1 text-sm text-slate-500">Pour recevoir le récapitulatif des devis</p>
              </div>
              <div>
                <input
                  type="text" inputMode="numeric" autoComplete="postal-code" maxLength={5}
                  value={postal} onChange={(e) => setPostal(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  placeholder="Code postal du chantier *" required
                  className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                <p className="mt-0.5 px-1 text-sm text-slate-500">Pour trouver les artisans proches de vous</p>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3.5 text-sm font-bold text-white shadow-lg transition-all hover:bg-primary-hover hover:shadow-xl disabled:opacity-50 btn-press"
              >
                {submitting ? (
                  <><span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" /> Recherche de pros...</>
                ) : (
                  <>Recevoir mes 3 devis gratuits <ArrowRight className="h-4 w-4" /></>
                )}
              </button>

              <div className="flex items-center justify-center gap-4 pt-1">
                <span className="flex items-center gap-1 text-[10px] text-slate-400"><Shield className="h-3 w-3 text-emerald-500" /> Gratuit</span>
                <span className="flex items-center gap-1 text-[10px] text-slate-400"><Clock className="h-3 w-3 text-blue-500" /> 48h</span>
                <span className="flex items-center gap-1 text-[10px] text-slate-400"><Star className="h-3 w-3 text-amber-500" /> Vérifiés</span>
              </div>
            </form>

            <button type="button" onClick={goBack} className="mt-3 flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-slate-400 hover:text-primary">
              <ArrowLeft className="h-3.5 w-3.5" /> Retour
            </button>
          </div>
        )}

        {/* Step 5: Confirmation */}
        {(step >= 5 || submitted) && (
          <div className="text-center py-2">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
              <CheckCircle className="h-8 w-8 text-emerald-500 animate-success-bounce" />
            </div>
            <h4 className="mt-3 text-base font-bold text-slate-900">Demande envoyée !</h4>
            <p className="mt-1 text-sm text-slate-500">{responseTimeText} — artisans vérifiés à {cityName}</p>

            {refNumber && (
              <p className="mt-2 inline-block rounded-lg bg-slate-100 px-3 py-1 font-mono text-xs text-slate-500">Réf. {refNumber}</p>
            )}

            {estimate && (
              <div className="mx-auto mt-4 max-w-xs rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-500">Estimation</p>
                <p className="mt-0.5 text-xl font-bold text-emerald-800">
                  <AnimatedPrice value={estimate.min} /> — <AnimatedPrice value={estimate.max} /> €
                </p>
              </div>
            )}

            {/* Timeline */}
            <div className="mx-auto mt-5 max-w-xs space-y-0">
              {[
                { icon: CheckCircle, text: 'Demande transmise aux artisans', done: true, color: 'text-emerald-500' },
                { icon: Phone, text: 'Un artisan vous appelle sous 24-48h', done: false, color: 'text-blue-400' },
                { icon: Star, text: 'Comparez et choisissez le meilleur', done: false, color: 'text-amber-400' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`flex h-7 w-7 items-center justify-center rounded-full ${item.done ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                      <item.icon className={`h-3.5 w-3.5 ${item.color}`} />
                    </div>
                    {i < 2 && <div className={`h-5 w-px ${item.done ? 'bg-emerald-200' : 'bg-slate-200'}`} />}
                  </div>
                  <p className={`pt-1 text-left text-xs ${item.done ? 'font-semibold text-slate-900' : 'text-slate-500'}`}>{item.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-5 flex items-center justify-center gap-4 text-[10px] text-slate-400">
              <span className="flex items-center gap-1"><Shield className="h-3 w-3 text-emerald-500" /> 100% gratuit</span>
              <span className="flex items-center gap-1"><Users className="h-3 w-3 text-blue-500" /> 3 devis max</span>
              <span className="flex items-center gap-1"><TrendingUp className="h-3 w-3 text-amber-500" /> Sans engagement</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
